import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { useSearchBarStyles } from './searchBar.styles';
import { Status } from '../../status';
import { IconButton } from '../../../foundations/iconButton';
import { Icon } from '@doxyme/icons';
import { useRef, type JSX } from 'react';
import { useIntl } from '@doxyme/locale';
import { Filter } from '../../filter';
import { type Option } from '../../dropdown';

export type SearchBarProps = {
  id?: string;
  size?: 'regular' | 'small';
  placeholder: string;
  value: string;
  disabled?: boolean;
  contentEditable?: boolean;
  hasError?: boolean;
  onChange: (value: string) => void;
  options?: Option[];
  selectedOption?: string | null;
  onOptionChanged?: (value: string) => void;
  className?: string;
};

export const SearchBar = ({
  size = 'small',
  onChange,
  value,
  disabled = false,
  contentEditable = true,
  hasError = false,
  options,
  selectedOption,
  onOptionChanged,
  className,
  ...rest
}: SearchBarProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { formatMessage } = useIntl();
  const styles = useSearchBarStyles({
    isFilled: Boolean(value),
    hasError,
    size,
    disabled,
    contentEditable,
  });

  const canEdit = Boolean(!disabled && contentEditable);

  const setFocus = (): void => {
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(0, inputRef.current.value.length);
    }, 0);
  };

  return (
    <TextField
      {...rest}
      className={className}
      disabled={!canEdit}
      value={value}
      error={hasError}
      inputRef={inputRef}
      css={styles.container}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        startAdornment: (
          <>
            {Boolean(options) && (
              <Filter
                filterButtonHasLabel
                options={options as Option[]}
                selected={selectedOption as string | null}
                onOptionClick={(newSelectionOption) => {
                  onOptionChanged?.(newSelectionOption);
                  setFocus();
                }}
              />
            )}
            <button
              type='button'
              aria-label={formatMessage({ id: 'input.searchBar.search' })}
              tabIndex={-1}
              css={{
                ...styles.adornment,
                ...styles.startAdornment,
              }}
              onClick={() => setFocus()}
            >
              <Icon.Search type='stroke' size='small' css={styles.searchIcon} />
            </button>
          </>
        ),
        endAdornment: (
          <Box css={styles.adornmentContainer}>
            {hasError && <Status statusLevel='error' size={20} />}
            {Boolean(value) && canEdit && (
              <IconButton
                icon={<Icon.Cross type='filled' size='small' />}
                type='subtle'
                size='medium'
                onClick={() => {
                  onChange('');
                  setFocus();
                }}
                title={formatMessage({ id: 'input.searchBar.clear' })}
              />
            )}
          </Box>
        ),
      }}
    />
  );
};
